import React, { useLayoutEffect } from 'react';
import { Box, Typography } from '@mui/material';
import QuoteSelectorComponent from '../QuoteSelector/QuoteSelectorComponent';
import { useNavigate } from 'react-router-dom';
import {
  LAUNCH_PAD_CATEGORY_ORDER,
  LAUNCH_PAD_PRODUCT_OPTIONS,
  getBaseProductRoute,
} from '../../../utils/productNavigation';
import { toSentenceCase, toTitleCase } from '../../../utils/casing';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

const LAUNCHPAD_BASE_URL = 'https://assets.askhippo.co.za/LaunchPad/';
const ICON_BASE_URL = 'https://assets.askhippo.co.za/LaunchPad/Icons/';

const launchPadImageConfig = {
  '/funeral-insurance-quote': {
    file: 'LaunchPadFuneral.svg',
    width: 551,
    height: 551,
  },
  '/life-insurance-quote': {
    file: 'LaunchPadLife.svg',
    width: 551,
    height: 551,
  },
  '/medical-gap-cover-quote': {
    file: 'LaunchPadGapCover.svg',
    width: 551,
    height: 551,
  },
  '/medical-insurance-quotes': {
    file: 'LaunchPadMedicalInsurance.svg',
    width: 551,
    height: 551,
  },
  '/medical-aid-quote': {
    file: 'LaunchPadMedicalAid.svg',
    width: 551,
    height: 551,
  },
  '/pet-insurance-quote': {
    file: 'LaunchPadPetInsurance.svg',
    width: 551,
    height: 551,
  },
  '/travel-insurance': {
    file: 'LaunchPadTravel.svg',
    width: 551,
    height: 551,
  },
  '/motor-warranty': {
    file: 'LaunchPadMotorWarranty.svg',
    width: 551,
    height: 551,
  },
  '/dents-and-scratches-quote': {
    file: 'LaunchPadScratchAndDent.svg',
    width: 551,
    height: 551,
  },
  '/tyre-and-rim-guard': {
    file: 'LaunchPadTyreAndRim.svg',
    width: 551,
    height: 551,
  },
  '/vehicle-tracking-quote': {
    file: 'LaunchPadVehicleTracking.svg',
    width: 551,
    height: 551,
  },
  '/service-plan-quotes': {
    file: 'LaunchPadServicePlan.svg',
    width: 551,
    height: 551,
  },
  '/buildings-insurance-quote': {
    file: 'LaunchPadBuildingInsurance.svg',
    width: 551,
    height: 551,
  },
  '/business-insurance-quote': {
    file: 'LaunchPadBusiness.svg',
    width: 551,
    height: 551,
  },
  '/compare-fibre-deals': {
    file: 'LaunchPadFibre.svg',
    width: 551,
    height: 551,
  },
  '/car-insurance-quote': {
    // file: 'LaunchPadCarInsuranceNew.svg',
    file: 'LaunchPadCarInsuranceV2.svg',
    width: 551,
    height: 551,
  },
  '/motorcycle-insurance-quote': {
    file: 'LaunchPadMotorCycle.svg',
    width: 551,
    height: 551,
  },
  '/home-insurance-quote': {
    file: 'LaunchPadHomeContents.svg',
    width: 551,
    height: 551,
  },
  '/online-insurance-quote': {
    file: 'LaunchPadComboInsurance.svg',
    width: 551,
    height: 551,
  },
  '/personal-loans': {
    file: 'LaunchPadPersonalLoans.svg',
    width: 551,
    height: 551,
  },
  '/debt-counselling': {
    file: 'LaunchPadDebtCounselling.svg',
    width: 551,
    height: 551,
  },
  '/debt-consolidation-loans': {
    file: 'LaunchPadDebtConsolidation.svg',
    width: 551,
    height: 551,
  },
  '/business-insurance-quote/car-and-commercial-property-insurance': {
    file: 'LaunchPadVehicleCommercial.svg',
    width: 551,
    height: 551,
  },
  '/employee-benefits-quote': {
    file: 'LaunchPadEmployeeBenefits.svg',
    width: 551,
    height: 551,
  },
  '/business-insurance-quote/business-legal-cover': {
    file: 'LaunchPadBusinessLegalCover.svg',
    width: 551,
    height: 551,
  },
  '/business-insurance-quote/professional-liability-insurance-cover': {
    file: 'LaunchPadProfessionalLiability.svg',
    width: 551,
    height: 551,
  },
  '/': {
    file: 'LaunchPadHome.svg',
    width: 635,
    height: 635,
  },
  '/insurance': {
    file: 'LaunchPadHome.svg',
    width: 635,
    height: 635,
  },
  '/motor': {
    file: 'LaunchPadHome.svg',
    width: 635,
    height: 635,
  },
  '/health': {
    file: 'LaunchPadHome.svg',
    width: 635,
    height: 635,
  },
  '/money': {
    file: 'LaunchPadHome.svg',
    width: 635,
    height: 635,
  },
  '/internet': {
    file: 'LaunchPadHome.svg',
    width: 635,
    height: 635,
  },
  default: {
    file: 'LaunchPadDefault.svg',
    width: 551,
    height: 540,
  },
};

const ImageIcon = ({ name, size = 24 }) => (
  <img
    src={`${ICON_BASE_URL}${encodeURIComponent(name)}.svg`}
    alt={`${name} icon`}
    width={size}
    height={size}
    style={{ display: 'block' }}
  />
);

const ICONS = {
  Motor: <ImageIcon name="car" />,
  Health: <ImageIcon name="medical-bag" />,
  Life: <ImageIcon name="family" />,
  Money: <ImageIcon name="Money" />,
  Business: <ImageIcon name="ShieldHeart" />,
  Internet: <ImageIcon name="Wifi" />,
};

const CATEGORY_LABELS = {
  Motor: 'Motor',
  Health: 'Health',
  Life: 'Life',
  Money: 'Money',
  Business: 'Business',
  Internet: 'Internet',
};

// Category landing pages that reuse the home page's LaunchPad layout
// (category icon row, home image sizing/position) with a custom header.
const HOME_STYLE_PATHS = [
  '/',
  '/insurance',
  '/motor',
  '/health',
  '/money',
  '/internet',
];

const LaunchPad = ({ pathName, hideDropdown = false, onImageLoad = null }) => {
  const navigate = useNavigate();
  const resolvedPathName = getBaseProductRoute(pathName);
  const isHomeStyle = HOME_STYLE_PATHS.includes(resolvedPathName);

  const productOptions = LAUNCH_PAD_PRODUCT_OPTIONS;

  const determineLine = () => {
    switch (resolvedPathName) {
      case '/car-insurance-quote':
        return {
          header: 'Compare 12 Car Insurance Quotes',
          byLine: 'Save R539 per month.',
          button: 'Get started',
          selectedOption: 'Car Insurance',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      case '/pet-insurance-quote':
        return {
          header: 'Find The Best Pet Insurance ',
          byLine:
            'Compare Pet Medical Aid plans from trusted providers and keep those tails wagging.',
          button: 'Get started',
          selectedOption: 'Pet Insurance',
          activeIcon: 'Life',
          hideDropdown: true,
        };
      case '/funeral-insurance-quote':
        return {
          header: 'Find the Best Funeral Insurance in Minutes',
          byLine:
            'Compare trusted Funeral cover quotes from leading South African providers. Hippo makes it simple, secure, and free to compare.',
          button: 'Get started',
          selectedOption: 'Funeral Insurance',
          activeIcon: 'Life',
          hideDropdown: true,
        };
      case '/life-insurance-quote':
        return {
          header: 'Find Better Life Insurance',
          byLine: 'Find the right life cover in just a few minutes ',
          button: 'Get started',
          selectedOption: 'Life Insurance',
          activeIcon: 'Life',
          hideDropdown: true,
        };
      case '/medical-aid-quote':
        return {
          header: 'Find the best medical aid',
          byLine:
            'Compare affordable medical aid plans and get quotes in under 2 minutes',
          button: 'Get started',
          selectedOption: 'Medical Aid',
          activeIcon: 'Health',
          hideDropdown: true,
        };
      case '/medical-gap-cover-quote':
        return {
          header: 'Find the Best Medical Gap Cover',
          byLine:
            'Compare medical gap cover insurance and make sure you’re never out of pocket after hospital visits.',
          button: 'Get started',
          selectedOption: 'Gap Cover',
          activeIcon: 'Health',
          hideDropdown: true,
        };
      case '/medical-insurance-quotes':
        return {
          header: 'Compare Medical Insurance Quotes',
          byLine: 'Find health cover that fits your needs and budget',
          button: 'Get started',
          selectedOption: 'Medical Insurance',
          activeIcon: 'Health',
          hideDropdown: true,
        };
      case '/travel-insurance':
        return {
          header: 'Find Better Travel Insurance ',
          byLine:
            'Compare quotes from trusted providers and enjoy peace of mind on every trip.',
          button: 'Get started',
          selectedOption: 'Travel Insurance',
          activeIcon: 'Life',
          hideDropdown: true,
        };
      case '/motor-warranty':
        return {
          header: 'Find The Best Motor Warranty Plan',
          byLine:
            'Compare warranty quotes from trusted providers and keep your car covered for less.',
          button: 'Get started',
          selectedOption: 'Motor Warranty',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      // Money
      case '/personal-loans':
        return {
          header: 'Compare Personal Loans in South Africa',
          byLine:
            'Compare loan amounts, repayment terms, and interest rates from trusted and vetted South African providers in minutes.',
          button: 'Get started',
          selectedOption: 'Personal Loans',
          activeIcon: 'Money',
          hideDropdown: true,
        };
      case '/debt-counselling':
        return {
          header: 'Debt Counselling in South Africa',
          byLine:
            'Understand debt review and compare trusted debt counselling options',
          button: 'Get started',
          selectedOption: 'Debt Counselling',
          activeIcon: 'Money',
          hideDropdown: true,
        };
      case '/debt-consolidation-loans':
        return {
          header: 'Debt Consolidation Loans',
          byLine:
            'Compare debt consolidation options from trusted South African providers',
          button: 'Get started',
          selectedOption: 'Debt Consolidation',
          activeIcon: 'Money',
          hideDropdown: true,
        };
      // Motor extras
      case '/dents-and-scratches-quote':
        return {
          header: 'Compare Scratch & Dent Cover Quotes',
          byLine:
            'Get minor damage cover that helps with unexpected dents and scratches',
          button: 'Get started',
          selectedOption: 'Scratch & Dent Cover',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      case '/tyre-and-rim-guard':
        return {
          header: 'Find Tyre and Rim Insurance',
          byLine: 'Get the right cover in just a few minutes!',
          button: 'Get started',
          selectedOption: 'Tyre & Rim Cover',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      case '/vehicle-tracking-quote':
        return {
          header: 'Find the Right Vehicle Tracker',
          byLine:
            'Compare car tracker quotes in mins. Find the right protection for you vehicle.',
          button: 'Get started',
          selectedOption: 'Vehicle Tracking',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      case '/service-plan-quotes':
        return {
          header: 'Find the Right Service Plan',
          byLine:
            'Compare quotes on service or motor plans in just a few minutes!',
          button: 'Get started',
          selectedOption: 'Service Plans',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      // Life
      case '/home-insurance-quote':
        return {
          header: 'Compare Home Insurance Quotes',
          byLine:
            'Protect your home with cover that fits your budget in minutes',
          button: 'Get started',
          selectedOption: 'Home Contents Cover',
          activeIcon: 'Life',
          hideDropdown: true,
        };
      case '/buildings-insurance-quote':
        return {
          header: 'Find Better\nBuildings Insurance',
          byLine:
            'Compare quotes from trusted insurers and protect your home from costly repairs and damages.',
          button: 'Get started',
          selectedOption: 'Building Insurance',
          activeIcon: 'Life',
          hideDropdown: true,
        };
      // Business
      case '/business-insurance-quote':
        return {
          header: 'Compare Business Insurance Quotes',
          byLine: 'Find the right cover for your business in minutes',
          button: 'Get started',
          selectedOption: 'Business Insurance',
          activeIcon: 'Business',
          hideDropdown: true,
        };
      case '/business-insurance-quote/business-legal-cover':
        return {
          header: 'Compare Business Legal Cover',
          byLine:
            'Get legal protection that fits your business needs in minutes',
          button: 'Get started',
          selectedOption: 'Business Legal Cover',
          activeIcon: 'Business',
          hideDropdown: true,
        };
      case '/business-insurance-quote/professional-liability-insurance-cover':
        return {
          header: 'Compare Professional Indemnity Quotes',
          byLine: 'Protect your business against professional liability claims',
          button: 'Get started',
          selectedOption: 'Professional Liability Insurance',
          activeIcon: 'Business',
          hideDropdown: true,
        };
      // Internet
      case '/compare-fibre-deals':
        return {
          header: 'Compare Fibre deals in minutes',
          byLine:
            'Find fast, reliable Fibre packages from leading providers and get the best speed and price for your address',
          button: 'Get started',
          selectedOption: 'Fibre Internet',
          activeIcon: 'Internet',
          hideDropdown: true,
        };
      // Motorcycle
      case '/motorcycle-insurance-quote':
        return {
          header: 'Compare Motorcycle Insurance Quotes',
          byLine: 'Get bike cover that fits your needs and budget in minutes',
          button: 'Get started',
          selectedOption: 'Motorbike Insurance',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      case '/employee-benefits-quote':
        return {
          header: 'Medical Aid for Your Employees',
          byLine: 'Compare corporate medical aid options for your team',
          button: 'Get started',
          selectedOption: 'Employee Benefits',
          activeIcon: 'Business',
          hideDropdown: true,
        };

      case '/business-insurance-quote/car-and-commercial-property-insurance':
        return {
          header: 'Compare Car & Commercial Property Insurance',
          byLine:
            'Get cover for your vehicles and business property in minutes',
          button: 'Get started',
          selectedOption: 'Commercial Property Insurance',
          activeIcon: 'Business',
          hideDropdown: true,
        };
      // Bundle
      case '/online-insurance-quote':
        return {
          header: 'Find better car & home bundles',
          byLine: 'Compare quotes in 2 minutes',
          button: 'Get started',
          selectedOption: 'Combo Insurance',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      case '/':
        return {
          header: 'Dunno if you can find better?',
          byLine: 'Compare multiple quotes in just 2 minutes',
          button: 'Get quotes',
          selectedOption: 'Car Insurance',
          activeIcon: 'Motor',
        };
      // Category landing pages - same layout as home, custom header + icon
      case '/insurance':
        return {
          header: 'Compare insurance with Hippo',
          byLine: 'Compare multiple quotes in just 2 minutes',
          button: 'Get quotes',
          selectedOption: 'Car Insurance',
          activeIcon: 'Motor',
        };
      case '/motor':
        return {
          header: 'Compare all motor cover with Hippo',
          byLine: 'Compare multiple quotes in just 2 minutes',
          button: 'Get quotes',
          selectedOption: 'Car Insurance',
          activeIcon: 'Motor',
        };
      case '/health':
        return {
          header: 'Compare healthcare products with Hippo',
          byLine: 'Compare multiple quotes in just 2 minutes',
          button: 'Get quotes',
          selectedOption: 'Medical Aid',
          activeIcon: 'Health',
        };
      case '/money':
        return {
          header: 'Compare money solutions with Hippo',
          byLine: 'Compare multiple quotes in just 2 minutes',
          button: 'Get quotes',
          selectedOption: 'Personal Loans',
          activeIcon: 'Money',
        };
      case '/internet':
        return {
          header: 'Compare internet providers with Hippo',
          byLine: 'Compare multiple quotes in just 2 minutes',
          button: 'Get quotes',
          selectedOption: 'Fibre Internet',
          activeIcon: 'Internet',
        };
      // Default / unknown routes
      default: {
        const lower = (resolvedPathName || '').toLowerCase();

        let inferredIcon = 'Motor';

        if (lower.includes('business')) {
          inferredIcon = 'Business';
        } else if (
          lower.includes('medical') ||
          lower.includes('health') ||
          lower.includes('gap')
        ) {
          inferredIcon = 'Health';
        } else if (
          lower.includes('life') ||
          lower.includes('funeral') ||
          lower.includes('travel') ||
          lower.includes('pet') ||
          lower.includes('home') ||
          lower.includes('building')
        ) {
          inferredIcon = 'Life';
        } else if (lower.includes('loan') || lower.includes('debt')) {
          inferredIcon = 'Money';
        } else if (lower.includes('fibre') || lower.includes('internet')) {
          inferredIcon = 'Internet';
        }

        return {
          header: 'Compare Quotes\nin 2 min',
          byLine:
            'Find better Insurance, Medical Aid, Loans, and more, in just 2 min.',
          button: 'Get quotes',
          selectedOption: '',
          activeIcon: inferredIcon,
        };
      }
    }
  };

  const determineImage = () => {
    const cfg =
      launchPadImageConfig[resolvedPathName] || launchPadImageConfig.default;

    const handleImageLoad = () => {
      if (typeof onImageLoad === 'function') {
        onImageLoad();
      }
    };

    return (
      <Image
        src={`${LAUNCHPAD_BASE_URL}${encodeURIComponent(cfg.file)}`}
        alt=""
        width={cfg.width}
        height={cfg.height}
        loading="eager"
        fetchPriority="high"
        onLoad={handleImageLoad}
        onError={handleImageLoad}
        style={{
          width: `${cfg.width}px`,
          height: `${cfg.height}px`,
          display: 'block',
        }}
      />
    );
  };

  const {
    header,
    byLine,
    button,
    selectedOption: initialSelectedOption,
    activeIcon: initialActiveIcon,
    hideDropdown: routeHideDropdown = false,
  } = determineLine();

  const effectiveHideDropdown = hideDropdown || routeHideDropdown;

  const [activeIcon, setActiveIcon] = React.useState(initialActiveIcon);
  const [selectedProduct, setSelectedProduct] = React.useState(
    initialSelectedOption
  );
  const headerRef = React.useRef(null);
  const [bottomSpacer, setBottomSpacer] = React.useState(0);

  React.useEffect(() => {
    const { selectedOption, activeIcon: newIcon } = determineLine();
    setActiveIcon(newIcon);
    setSelectedProduct(selectedOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedPathName]);

  useLayoutEffect(() => {
    if (!effectiveHideDropdown) {
      setBottomSpacer(0);
      return undefined;
    }

    const cfg =
      launchPadImageConfig[resolvedPathName] || launchPadImageConfig.default;
    const imageTopOffset = isHomeStyle ? 90 : 82;
    const imageBottomExtent = cfg.height - imageTopOffset;
    const reservedGap = 150;

    const updateSpacer = () => {
      const headerHeight =
        headerRef.current?.getBoundingClientRect().height || 0;
      setBottomSpacer(
        Math.max(0, imageBottomExtent - headerHeight - reservedGap)
      );
    };

    updateSpacer();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSpacer);
      return () => window.removeEventListener('resize', updateSpacer);
    }

    const observer = new ResizeObserver(updateSpacer);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    window.addEventListener('resize', updateSpacer);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateSpacer);
    };
  }, [effectiveHideDropdown, resolvedPathName]);

  const productRoutes = {
    'Motorbike Insurance': '/motorcycle-insurance-quote/',
    'Tyre & Rim Cover': '/tyre-and-rim-guard/',
    'Scratch & Dent Cover': '/dents-and-scratches-quote/',
    'Car Insurance': '/car-insurance-quote/',
    'Combo Insurance': '/online-insurance-quote/',
    'Motor Warranty': '/motor-warranty/',
    // 'Car Rental': '/CarRental/',
    'Service Plans': '/service-plan-quotes/',
    'Vehicle Tracking': '/vehicle-tracking-quote/',
    'Home Contents Cover': '/home-insurance-quote/',
    'Building Insurance': '/buildings-insurance-quote/',
    'Business Insurance': '/business-insurance-quote/',
    'Commercial Property Insurance':
      '/business-insurance-quote/car-and-commercial-property-insurance/',
    // 'Vehicle Insurance': '/VehicleInsurance/',
    'Business Legal Cover': '/business-insurance-quote/business-legal-cover/',
    'Professional Liability Insurance':
      '/business-insurance-quote/professional-liability-insurance-cover/',
    // 'Business Loans': '/BusinessLoans/',
    'Employee Benefits': '/employee-benefits-quote/',
    'Medical Insurance': '/medical-insurance-quotes/',
    'Medical Aid': '/medical-aid-quote/',
    'Gap Cover': '/medical-gap-cover-quote/',
    'Life Insurance': '/life-insurance-quote/',
    'Funeral Insurance': '/funeral-insurance-quote/',
    'Pet Insurance': '/pet-insurance-quote/',
    'Travel Insurance': '/travel-insurance/',
    'Personal Loans': '/personal-loans/',
    'Debt Counselling': '/debt-counselling/',
    'Debt Consolidation': '/debt-consolidation-loans/',
    'Fibre Internet': '/compare-fibre-deals/',
  };

  // Fibre's quote-flow steps live under a different base path than its
  // landing page (/check-fibre-in-your-area vs /compare-fibre-deals); every
  // other product shares the same base for both, so this only needs an
  // entry when the two diverge.
  const productStepRoutes = {
    'Fibre Internet': '/check-fibre-in-your-area/',
  };

  const handleQuoteChange = (product) => {
    setSelectedProduct(product);
  };

  const handleQuote = () => {
    if (!selectedProduct) return;
    const route = productRoutes[selectedProduct];
    const currentBaseRoute = getBaseProductRoute(pathName);

    if (!route) {
      return;
    }

    if (route.replace(/\/+$/, '') !== currentBaseRoute) {
      navigate(route);
      return;
    }

    const stepBaseRoute = productStepRoutes[selectedProduct] || route;
    navigate(`${stepBaseRoute.replace(/\/+$/, '')}/step1`);
  };

  const handleCategorySelect = (category) => {
    setActiveIcon(category);
    const firstOption = productOptions[category]?.[0] || '';
    setSelectedProduct(firstOption);
  };

  return (
    <ScrollFadeWrapper
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box
        component="header"
        role="banner"
        sx={{
          position: 'relative',
          width: '1113px',
          backgroundColor: '#FFFFFF',
          borderRadius: '48px',
          p: '40px',
          overflow: 'visible',
          mt: '40px',
          mr: isHomeStyle ? '120px' : '80px',
          minHeight: effectiveHideDropdown ? 'auto' : '419px',
          zIndex: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: effectiveHideDropdown
            ? 'flex-start'
            : 'space-between',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: isHomeStyle ? '-120px' : '-82px',
            right: isHomeStyle ? '-60px' : '-100px',
            width: '517px',
            height: '517px',
            transformOrigin: 'top right',
            zIndex: 1,
          }}
          aria-hidden="true"
        >
          <ScrollFadeWrapper delay={0.15} startingOpacity={0.5}>
            {determineImage()}
          </ScrollFadeWrapper>
        </Box>

        <Box
          sx={{
            zIndex: 3,
            width: '659px',
            boxSizing: 'border-box',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            flex: '0 0 auto',
          }}
          ref={headerRef}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Pally, Pally-Fallback, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '2.25rem', md: '4.5rem' },
                lineHeight: { xs: '2.5rem', md: '4.25rem' },
                letterSpacing: '-0.03em',
                whiteSpace: 'pre-line',
                mb: '16px',
                width: '100%',
                maxWidth: '100%',
              }}
            >
              {toSentenceCase(header)}
            </Typography>

            <Typography
              component="h6"
              sx={{
                fontFamily: 'Inter, Inter-Fallback, sans-serif',
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: '1.5rem',
                letterSpacing: '-0.03em',
                mb: effectiveHideDropdown
                  ? '32px'
                  : isHomeStyle
                    ? '32px'
                    : '40px',
                whiteSpace: 'pre-line',
                width: '100%',
              }}
            >
              {toSentenceCase(byLine)}
            </Typography>
          </Box>

          {isHomeStyle && (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                px: '48.5px',
                boxSizing: 'border-box',
                gap: '32px',
                flexWrap: 'nowrap',
                m: 0,
                mb: '16px',
                justifyContent: 'center',
              }}
            >
              {LAUNCH_PAD_CATEGORY_ORDER.map((label) => {
                const icon = ICONS[label];
                const displayLabel = toTitleCase(
                  CATEGORY_LABELS[label] || label
                );
                const isActive = activeIcon === label;
                return (
                  <Box
                    key={label}
                    onClick={() => handleCategorySelect(label)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: isActive ? '#C7CA00' : '#E3E6E8',
                        width: '48px',
                        height: '48px',
                        borderRadius: '20px',
                        padding: '12px',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <Box sx={{ width: 24, height: 24 }}>{icon}</Box>
                    </Box>

                    <Typography
                      component="span"
                      sx={{
                        fontFamily: 'Inter, Inter-Fallback, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.625rem',
                        textTransform: 'none',
                      }}
                    >
                      {displayLabel}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0 }}>
            <QuoteSelectorComponent
              value={selectedProduct}
              onChange={handleQuoteChange}
              onQuote={handleQuote}
              options={productOptions[activeIcon] || []}
              placeholder={`Select ${
                (CATEGORY_LABELS[activeIcon] || activeIcon || 'Hippo') ===
                'Internet'
                  ? 'an'
                  : 'a'
              } ${CATEGORY_LABELS[activeIcon] || activeIcon || 'Hippo'} Product`}
              buttonLabel={button}
              hideDropdown={effectiveHideDropdown}
            />
          </Box>
        </Box>
      </Box>
      {effectiveHideDropdown && (
        <Box
          aria-hidden="true"
          sx={{
            width: '1113px',
            height: `${bottomSpacer}px`,
            flexShrink: 0,
            pointerEvents: 'none',
          }}
        />
      )}
    </ScrollFadeWrapper>
  );
};

export default LaunchPad;
